// src/components/student/AddStudent.jsx
import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { addStudent } from '../../services/studentService';
import { getAllDrivers } from '../../services/driverService';
import { PROGRAMS, GRADES } from '../../utils/constants';
import { styles } from '../../styles/theme';
import { Input, Select, Textarea, Checkbox } from '../shared/FormField';

const AddStudent = ({ onBack, onSuccess }) => {
  const [drivers, setDrivers] = useState([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    grade: '',
    dateOfBirth: '',
    gender: '',
    medicalConditions: '',
    parentFirstName: '',
    parentLastName: '',
    parentPhone: '',
    parentEmail: '',
    schoolName: '',
    schoolAddress: '',
    schoolPhone: '',
    programSelection: '',
    needsPickup: false,
    pickupDays: '',
    assignedDriver: '',
    authorizedPickup: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    getAllDrivers()
      .then(data => setDrivers(data.filter(d => d.status === 'active')))
      .catch(() => setDrivers([]));
  }, []);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.firstName || !formData.lastName || !formData.grade) {
      alert('Please fill in all required fields (First Name, Last Name, Grade)');
      return;
    }

    setIsSubmitting(true);

    try {
      const studentData = {
        ...formData,
        parentName: `${formData.parentFirstName} ${formData.parentLastName}`,
        submissionDate: new Date(),
        status: 'pending',
        archived: false
      };

      await addStudent(studentData);
      alert('✅ Student added successfully!');
      onSuccess?.();
    } catch (error) {
      console.error('Error adding student:', error);
      alert('❌ Failed to add student. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.centerContainer}>
          <button style={styles.backButton} onClick={onBack}>
            ← Back to Portal
          </button>
          <h1 style={styles.headerTitle}>Add New Student</h1>
          <p style={styles.headerSubtitle}>Register a new student to the program</p>
        </div>
      </div>

      <div style={styles.centerContainer}>
        <div style={styles.card}>
          <form onSubmit={handleSubmit}>
            {/* Student Information */}
            <h2 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Plus style={{ width: '20px', height: '20px', color: '#3b82f6' }} />
              Student Information
            </h2>

            <div style={styles.gridTwoCols}>
              <Input
                label="First Name"
                value={formData.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                required
              />
              <Input
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                required
              />
            </div>

            <div style={styles.gridTwoCols}>
              <Select
                label="Grade"
                value={formData.grade}
                onChange={(e) => handleChange('grade', e.target.value)}
                options={GRADES}
                required
              />
              <Input
                label="Date of Birth"
                type="date"
                value={formData.dateOfBirth}
                onChange={(e) => handleChange('dateOfBirth', e.target.value)}
              />
            </div>

            <Select
              label="Gender"
              value={formData.gender}
              onChange={(e) => handleChange('gender', e.target.value)}
              options={['Male', 'Female', 'Other', 'Prefer not to say']}
            />

            <Textarea
              label="Medical Conditions / Allergies"
              value={formData.medicalConditions}
              onChange={(e) => handleChange('medicalConditions', e.target.value)}
              placeholder="Enter any medical conditions, allergies, or special needs (or 'None')"
              rows={3}
            />

            {/* Parent Information */}
            <h2 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Parent/Guardian Information</h2>

            <div style={styles.gridTwoCols}>
              <Input
                label="Parent First Name"
                value={formData.parentFirstName}
                onChange={(e) => handleChange('parentFirstName', e.target.value)}
                required
              />
              <Input
                label="Parent Last Name"
                value={formData.parentLastName}
                onChange={(e) => handleChange('parentLastName', e.target.value)}
                required
              />
            </div>

            <div style={styles.gridTwoCols}>
              <Input
                label="Phone Number"
                type="tel"
                value={formData.parentPhone}
                onChange={(e) => handleChange('parentPhone', e.target.value)}
                required
              />
              <Input
                label="Email Address"
                type="email"
                value={formData.parentEmail}
                onChange={(e) => handleChange('parentEmail', e.target.value)}
                required
              />
            </div>

            <Input
              label="Authorized Pickup Persons"
              value={formData.authorizedPickup}
              onChange={(e) => handleChange('authorizedPickup', e.target.value)}
              placeholder="Enter names separated by commas"
            />

            {/* School Information */}
            <h2 style={{ marginTop: '2rem', marginBottom: '1rem' }}>School Information</h2>

            <Input
              label="School Name"
              value={formData.schoolName}
              onChange={(e) => handleChange('schoolName', e.target.value)}
            />

            <Input
              label="School Address"
              value={formData.schoolAddress}
              onChange={(e) => handleChange('schoolAddress', e.target.value)}
            />

            <Input
              label="School Phone"
              type="tel"
              value={formData.schoolPhone}
              onChange={(e) => handleChange('schoolPhone', e.target.value)}
            />

            {/* Program Information */}
            <h2 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Program Information</h2>

            <Select
              label="Program Selection"
              value={formData.programSelection}
              onChange={(e) => handleChange('programSelection', e.target.value)}
              options={PROGRAMS.map(p => p.name)}
              required
            />

            <Checkbox
              label="Student needs pickup service"
              checked={formData.needsPickup}
              onChange={(e) => handleChange('needsPickup', e.target.checked)}
            />

            {formData.needsPickup && (
              <>
                <Input
                  label="Pickup Days"
                  value={formData.pickupDays}
                  onChange={(e) => handleChange('pickupDays', e.target.value)}
                  placeholder="e.g., Monday, Tuesday, Friday"
                />

                <Select
                  label="Assigned Driver"
                  value={formData.assignedDriver}
                  onChange={(e) => handleChange('assignedDriver', e.target.value)}
                  options={drivers.map(d => ({ value: d.id, label: d.name }))}
                  placeholder="Select driver..."
                />
              </>
            )}

            {/* Submit Buttons */}
            <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
              <button
                type="button"
                style={{
                  ...styles.button,
                  flex: 1,
                  padding: '1rem',
                  fontSize: '1rem',
                  backgroundColor: '#6b7280'
                }}
                onClick={onBack}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  ...styles.buttonGreen,
                  flex: 1,
                  padding: '1rem',
                  fontSize: '1rem'
                }}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Adding Student...' : 'Add Student'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddStudent;